import { RefObject, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import SpaceBarOutlinedIcon from "@mui/icons-material/SpaceBarOutlined";
import { presetBreakValues } from "shared/models";
import { SSMLTag, SSMLTagsColors, timeOptions } from "shared/constants";
import { useIsActiveSSMLTag } from "shared/hooks/useSSMLTag";
import {
  handleSSMLValueClick,
  isEditorEmpty,
} from "shared/utils/tts-editor-helper";
import PresetCustomMenu from "features/TTS/TTSCustomEditor/components/PresetCustomMenu";
import MenuWrapper from "../../components/MenuWrapper";
import { useSetAtom } from "jotai";
import { shouldCloseMenuAtom } from "atoms";
import {
  getBreakTimeValue,
  getPresetBreakValue,
  extractTimeValueAndUnit,
} from "shared/utils/tts-custom-editor";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import CustomIconTextButtonMenu from "../../components/CustomIconTextButtonMenu";

interface BreakMenuProps {
  editorRef: RefObject<EditorRef | null>;
}

export default function BreakMenu({ editorRef }: BreakMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [timeUnit, setTimeUnit] = useState<string>(timeOptions[0]);
  const [customValue, setCustomValue] = useState<string>("");
  const [activeBreakTagId, setActiveBreakTagId] = useState<string | null>(null);
  const [activeBreakValue, setActiveBreakValue] = useState<string>("");
  const isBreakTagActive = useIsActiveSSMLTag(SSMLTag.Break);
  const setShouldCloseMenu = useSetAtom(shouldCloseMenuAtom);
  const editorEmpty = isEditorEmpty(editorRef);

  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.Break);

  // Break tags should always be editable when clicked/active
  const shouldDisableMenu = isBreakTagActive ? false : isSelectionDisabled;

  const timeOptionPlaceholders = [
    {
      unit: "ms",
      placeholder: t("tts.toolbarMenu.breakMenu.breakMsPlaceholder"),
    },
    {
      unit: "s",
      placeholder: t("tts.toolbarMenu.breakMenu.breakSPlaceholder"),
    },
  ];

  useEffect(() => {
    if (isBreakTagActive && editorRef.current?.editor) {
      const editor = editorRef.current.editor;

      // Get the currently selected node
      const selectedNode = editor.selection.getNode();

      let breakNode = null;

      if (selectedNode.classList?.contains("ssml-break")) {
        breakNode = selectedNode;
      } else {
        const breakNodes = editor.dom.select("span.ssml-break");
        const selection = editor.selection;
        const range = selection.getRng();

        // Find the break node that's either selected or contains the cursor
        for (const node of breakNodes) {
          const nodeRange = document.createRange();
          nodeRange.selectNode(node);

          if (
            range.compareBoundaryPoints(Range.START_TO_START, nodeRange) >= 0 &&
            range.compareBoundaryPoints(Range.END_TO_END, nodeRange) <= 0
          ) {
            breakNode = node;
            break;
          }
        }
      }

      if (breakNode) {
        const dataSsml = breakNode.getAttribute("data-ssml");
        if (dataSsml) {
          const parts = dataSsml.split("=");
          if (parts.length === 2) {
            setActiveBreakTagId(parts[0]);

            const currentBreakValue = parts[1];
            setActiveBreakValue(currentBreakValue);

            const isPreset = presetBreakValues.some(
              (preset) => preset.value === currentBreakValue
            );

            if (!isPreset) {
              const { value, unit } =
                extractTimeValueAndUnit(currentBreakValue);
              if (value) {
                setCustomValue(value);
                if (unit && timeOptions.includes(unit)) {
                  setTimeUnit(unit);
                }
              }
            } else {
              setCustomValue("");
            }
          }
        }
      }
    } else {
      // Reset state when menu closes or break tag is not active
      setActiveBreakTagId(null);
      setActiveBreakValue("");
      setCustomValue("");
    }
  }, [isBreakTagActive, editorRef]);

  const handleBreakValueClick = (value: string): void => {
    if (!editorRef.current || value.trim().length === 0) return;
    const editor = editorRef.current.editor;
    if (!editor) return;
    const presetBreakContent = getPresetBreakValue(value);
    let breakTime = value;
    if (!presetBreakContent) {
      breakTime = getBreakTimeValue(value, timeUnit);
    }
    // If an active break tag ID exists, update it
    if (activeBreakTagId && isBreakTagActive) {
      const breakNodes = editor.dom.select(
        `span[data-ssml^="${activeBreakTagId}"]`
      );

      const selectedNode = editor.selection.getNode();

      if (
        selectedNode.classList?.contains("ssml-break") &&
        selectedNode.getAttribute("data-ssml")?.startsWith(activeBreakTagId)
      ) {
        selectedNode.setAttribute(
          "data-ssml",
          `${activeBreakTagId}=${breakTime}`
        );
        selectedNode.textContent = `[${breakTime}]`;
        editor.fire("change");
        setShouldCloseMenu(true);
        return;
      }

      if (breakNodes.length > 0) {
        const node = breakNodes[0];
        node.setAttribute("data-ssml", `${activeBreakTagId}=${breakTime}`);
        node.textContent = `[${breakTime}]`;
        editor.fire("change");
        setShouldCloseMenu(true);
        return;
      }
    }

    // If no active break tag was found or updated, insert a new one
    handleSSMLValueClick(
      editorRef,
      SSMLTag.Break,
      presetBreakContent?.value || breakTime,
      SSMLTagsColors.BreakTag,
      []
    );
    setShouldCloseMenu(true);
  };

  // Check if the active value is a custom value (not in presets)
  const isCustomBreakValueActive = Boolean(
    activeBreakValue &&
      !presetBreakValues.some((preset) => preset.value === activeBreakValue)
  );

  return (
    <CustomIconTextButtonMenu
      componentId="break-pause"
      menuTitle={t("tts.toolbarMenu.breakMenu.title")}
      icon={SpaceBarOutlinedIcon}
      editorRef={editorRef}
      isActive={isBreakTagActive}
      isEditorEmpty={editorEmpty}
      shouldBeDisabled={shouldDisableMenu}
      tagType={SSMLTag.Break}
      isSelectionDisabled={shouldDisableMenu}
      showChevronIcon
    >
      <MenuWrapper
        id="break-menu-wrapper"
        showDeleteButton={isBreakTagActive}
        editorRef={editorRef}
        tagType={SSMLTag.Break}
      >
        <PresetCustomMenu
          showPresetMenuList
          showCustomTextField
          presetHeader={t("tts.toolbarMenu.breakMenu.presetBreakHeader")}
          presetValues={presetBreakValues}
          customHeader={t("tts.toolbarMenu.breakMenu.customBreakHeader")}
          handleClick={handleBreakValueClick}
          unitOptions={timeOptions}
          selectedUnit={timeUnit}
          setSelectedUnit={setTimeUnit}
          unitPlaceholders={timeOptionPlaceholders}
          showCheckButton
          defaultValue={customValue}
          value={customValue}
          activeValue={activeBreakValue}
          isCustomValueActive={isCustomBreakValueActive}
        />
      </MenuWrapper>
    </CustomIconTextButtonMenu>
  );
}
